import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useState, useEffect } from 'react';
import { X, Loader, Plus } from 'lucide-react';
import { supabase } from '../../lib/supabase';
/**
 * SubItemsDrawer Component
 *
 * Bottom drawer showing sub-items based on the active task type:
 * - If step: shows associated breaths
 * - If task_list: shows associated steps
 * - If length: shows associated steps
 * - If breath: shows nothing (breaths don't have sub-items)
 *
 * Allows quick adding of sub-items directly to queue.
 */
export function SubItemsDrawer({ isOpen, onClose, activeItem, onAddSubItem }) {
    const [subItems, setSubItems] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isAdding, setIsAdding] = useState(null);
    // Fetch sub-items when drawer opens or active item changes
    useEffect(() => {
        if (!isOpen || !activeItem) {
            setSubItems([]);
            return;
        }
        const fetchSubItems = async () => {
            try {
                setIsLoading(true);
                switch (activeItem.itemType) {
                    case 'step': {
                        // Get breaths for this step
                        const { data } = await supabase
                            .from('breaths')
                            .select('id, name, time_estimation_seconds')
                            .eq('step_id', activeItem.itemId)
                            .order('position', { ascending: true });
                        if (data) {
                            setSubItems(data.map(breath => ({
                                id: breath.id,
                                label: breath.name,
                                duration: breath.time_estimation_seconds
                            })));
                        }
                        break;
                    }
                    case 'task_list': {
                        // Get steps for this task list
                        const { data } = await supabase
                            .from('steps')
                            .select('id, label, duration')
                            .eq('task_list_id', activeItem.itemId)
                            .eq('completed', false)
                            .order('position', { ascending: true });
                        if (data) {
                            setSubItems(data.map(step => ({
                                id: step.id,
                                label: step.label,
                                duration: step.duration
                            })));
                        }
                        break;
                    }
                    case 'length': {
                        // Get steps for this length
                        const { data } = await supabase
                            .from('steps')
                            .select('id, label, duration')
                            .eq('length_id', activeItem.itemId)
                            .eq('completed', false)
                            .order('position', { ascending: true });
                        if (data) {
                            setSubItems(data.map(step => ({
                                id: step.id,
                                label: step.label,
                                duration: step.duration
                            })));
                        }
                        break;
                    }
                    case 'breath':
                        // Breaths don't have sub-items
                        setSubItems([]);
                        break;
                }
            }
            finally {
                setIsLoading(false);
            }
        };
        fetchSubItems();
    }, [isOpen, activeItem]);
    if (!isOpen)
        return null;
    const getSubItemType = () => {
        switch (activeItem?.itemType) {
            case 'step':
                return 'breath';
            case 'task_list':
            case 'length':
                return 'step';
            default:
                return '';
        }
    };
    const getSubItemLabel = () => {
        switch (activeItem?.itemType) {
            case 'step':
                return 'Breaths';
            case 'task_list':
            case 'length':
                return 'Steps';
            default:
                return 'Sub-items';
        }
    };
    const handleAddSubItem = async (subItemId) => {
        if (!onAddSubItem)
            return;
        setIsAdding(subItemId);
        try {
            await onAddSubItem(getSubItemType(), subItemId);
        }
        finally {
            setIsAdding(null);
        }
    };
    return (_jsxs("div", { className: "fixed inset-0 z-50", children: [_jsx("div", { className: "absolute inset-0 bg-black/30 transition-opacity", onClick: onClose }), _jsxs("div", { className: "absolute bottom-0 left-0 right-0 bg-white rounded-t-2xl shadow-2xl animate-in slide-in-from-bottom", children: [_jsxs("div", { className: "px-6 py-4 border-b border-slate-200 flex items-center justify-between", children: [_jsxs("div", { children: [_jsx("h2", { className: "text-lg font-bold text-slate-900", children: getSubItemLabel() }), activeItem && (_jsxs("p", { className: "text-sm text-slate-600 mt-1", children: ["From: ", activeItem.label] }))] }), _jsx("button", { onClick: onClose, className: "p-1 hover:bg-slate-100 rounded transition-colors", children: _jsx(X, { className: "w-5 h-5 text-slate-600" }) })] }), _jsx("div", { className: "max-h-64 overflow-y-auto", children: isLoading ? (_jsx("div", { className: "flex items-center justify-center h-32", children: _jsx(Loader, { className: "w-6 h-6 text-blue-500 animate-spin" }) })) : subItems.length === 0 ? (_jsxs("div", { className: "flex flex-col items-center justify-center h-32 text-slate-500 p-4", children: [_jsxs("p", { className: "font-medium", children: ["No ", getSubItemLabel().toLowerCase(), " found"] }), _jsx("p", { className: "text-sm", children: activeItem?.itemType === 'breath'
                                        ? 'Breaths don\'t have sub-items'
                                        : `Add ${getSubItemLabel().toLowerCase()} to ${activeItem?.label}` })] })) : (_jsx("div", { className: "space-y-2 p-4", children: subItems.map(subItem => {
                                const minutes = Math.floor((subItem.duration || 0) / 60);
                                const seconds = (subItem.duration || 0) % 60;
                                const durationText = subItem.duration
                                    ? `${minutes}:${String(seconds).padStart(2, '0')}`
                                    : '—';
                                return (_jsxs("div", { className: "flex items-center justify-between p-3 bg-slate-50 hover:bg-slate-100 rounded-lg transition-colors", children: [_jsxs("div", { className: "flex-1 min-w-0", children: [_jsx("h4", { className: "font-medium text-slate-900 truncate text-sm", children: subItem.label }), subItem.duration && (_jsxs("p", { className: "text-xs text-slate-500 mt-1", children: ["Duration: ", durationText] }))] }), _jsx("button", { onClick: () => handleAddSubItem(subItem.id), disabled: isAdding === subItem.id, className: "ml-2 p-2 bg-blue-500 hover:bg-blue-600 disabled:bg-blue-300 text-white rounded transition-colors flex-shrink-0", title: "Add to queue", children: isAdding === subItem.id ? (_jsx(Loader, { className: "w-4 h-4 animate-spin" })) : (_jsx(Plus, { className: "w-4 h-4" })) })] }, subItem.id));
                            }) })) }), _jsx("div", { className: "h-4" })] })] }));
}
